/** Observe scoped durable receipts independently of the dialog; receipts alone update the Tests cache. */
import { useQueries, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { shopifyQueryPolicies } from '../../queries/shopifyQueryPolicies'
import type { StudyAuthoringDeletionReceipt } from '@testfeed/shopify-contracts'
import type { ShopifyAppBridge } from '../../shopifyApiFetch'
import type { VerifiedTestFeedConnectionScope } from '../../ShopifyAppContext'
import type { SavedStudyBuilderDraftSummary } from './studyBuilderDrafts'
import { studyAuthoringQueryPrefix } from './useStudyAuthoringEditor'
import { readStudyAuthoringDeletionStatus } from './studyAuthoringClient'
import { studyDeletionReceipt } from '../../components/study-deletion-eligibility'
import { trackShopifyAction } from '../../shopifyUiTracking'

/** Apply only authoritative receipts; pending rows retain their name/product but cannot be edited or selected. */
export function projectStudyDeletionReceipts(
  rows: SavedStudyBuilderDraftSummary[],
  receipts: StudyAuthoringDeletionReceipt[],
): SavedStudyBuilderDraftSummary[] {
  const byStudy = new Map(receipts.map((receipt) => [receipt.studyId, receipt]))
  return rows.flatMap((row) => {
    const receipt = row.studyId ? byStudy.get(row.studyId) : undefined
    if (!receipt) return [row]
    if (receipt.status === 'completed') return []
    return [{ ...row, state: undefined, summary: undefined, deletion: receipt }]
  })
}

/** A list read that started before cleanup must not resurrect an already-confirmed deleted row. */
export function cachedStudyDeletionReceipts(client: QueryClient, scope: VerifiedTestFeedConnectionScope) {
  return client
    .getQueriesData<StudyAuthoringDeletionReceipt>({
      queryKey: [...studyAuthoringQueryPrefix(scope), 'deletion'],
    })
    .flatMap(([, receipt]) => (receipt?.status === 'completed' ? [receipt] : []))
}

export function useStudyDeletionReceipts(
  shopify: ShopifyAppBridge,
  scope: VerifiedTestFeedConnectionScope | null,
  drafts: SavedStudyBuilderDraftSummary[],
  items: { id: string; operationId?: string; status?: string }[],
) {
  const client = useQueryClient()
  const targets = new Map<
    string,
    { studyId: string; operationId: string; pending?: boolean; receipt?: StudyAuthoringDeletionReceipt }
  >()
  for (const draft of drafts) {
    const receipt = studyDeletionReceipt(draft)
    if (receipt && receipt.status !== 'completed')
      targets.set(receipt.operationId, {
        studyId: draft.studyId ?? draft.draftId,
        operationId: receipt.operationId,
        pending: receipt.status === 'accepted' || receipt.status === 'cleaning',
        receipt: 'label' in receipt ? receipt : undefined,
      })
  }
  for (const item of items) {
    if (item.operationId && !targets.has(item.operationId))
      targets.set(item.operationId, { studyId: item.id, operationId: item.operationId })
  }
  const entries = [...targets.values()]
  const queries = useQueries({
    queries: entries.map((target) => ({
      ...shopifyQueryPolicies.deletionStatus,
      queryKey: [
        ...(scope ? studyAuthoringQueryPrefix(scope) : ['shopify-study-authoring', 'no-scope']),
        'deletion',
        target.operationId,
      ],
      enabled: Boolean(scope),
      initialData: target.receipt,
      retry: false,
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const receipt = await trackShopifyAction(shopify, 'check-deletion', () =>
          readStudyAuthoringDeletionStatus(
            shopify,
            { studyId: target.studyId, operationId: target.operationId },
            signal,
          ),
        )
        signal.throwIfAborted()
        if (scope)
          client.setQueryData<SavedStudyBuilderDraftSummary[]>(
            [...studyAuthoringQueryPrefix(scope), 'list'],
            (current) => (current ? projectStudyDeletionReceipts(current, [receipt]) : current),
          )
        return receipt
      },
      refetchInterval: (query: { state: { data?: StudyAuthoringDeletionReceipt } }) => {
        const status = query.state.data?.status
        if (status === 'completed') return false
        // A fresh list can show a failed receipt was re-armed in another visit.
        if (status === 'failed' && !target.pending) return false
        return 2000
      },
    })),
  })
  return new Map(entries.map((target, index) => [target.operationId, queries[index]!]))
}
